import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Chip,
  CircularProgress, Alert, Button, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { getTasks, submitTask } from '../services/api';

const statusColor = {
  assigned: '#2563eb',
  submitted: '#d97706',
  approved: '#16a34a',
  rejected: '#dc2626'
};

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialog, setDialog] = useState({ open: false, taskId: null });
  const [submission, setSubmission] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const role = localStorage.getItem('role');

  const load = async () => {
    try {
      const res = await getTasks();
      setTasks(res.data);
    } catch {
      setError('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await submitTask(dialog.taskId, submission);
      setDialog({ open: false, taskId: null });
      setSubmission('');
      load();
    } catch {
      setError('Failed to submit task');
    } finally {
      setSubmitting(false);
    }
  };

  const parseBrief = (json) => {
    try { return JSON.parse(json); }
    catch { return null; }
  };

  if (loading) return (
    <Box display="flex" justifyContent="center" mt={4}>
      <CircularProgress sx={{ color: '#2563eb' }} />
    </Box>
  );

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" color="#fff" mb={3}>
        {role === 'owner' ? 'All Tasks' : 'My Tasks'}
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {tasks.length === 0 && (
        <Card sx={{ background: '#1a2633' }}>
          <CardContent>
            <Typography color="#8899aa">No tasks found</Typography>
          </CardContent>
        </Card>
      )}
      {tasks.map((t) => {
        const brief = parseBrief(t.final_brief_json || t.original_brief_json);
        return (
          <Card key={t.id} sx={{ background: '#1a2633', mb: 2, border: '1px solid #2d3f50' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Box display="flex" gap={1}>
                  <Chip
                    label={t.status?.toUpperCase()}
                    size="small"
                    sx={{ background: statusColor[t.status] || '#6b7280', color: '#fff' }}
                  />
                  <Chip
                    label={t.role}
                    size="small"
                    sx={{ background: '#2d3f50', color: '#8899aa' }}
                  />
                </Box>
                <Typography variant="body2" color="#8899aa">
                  {new Date(t.created_at).toLocaleString()}
                </Typography>
              </Box>

              <Typography color="#fff" fontWeight="bold" mb={1}>
                Task {t.id}
              </Typography>

              {brief && (
                <Box sx={{ background: '#0f1923', p: 2, borderRadius: 1, mb: 2 }}>
                  <Typography color="#8899aa" variant="body2" mb={1}>
                    <strong style={{ color: '#fff' }}>Instructions:</strong>{' '}
                    {brief.instructions}
                  </Typography>
                  {brief.success_criteria && (
                    <Box>
                      <Typography color="#fff" variant="body2" fontWeight="bold">
                        Success Criteria:
                      </Typography>
                      {brief.success_criteria.map((c, i) => (
                        <Typography key={i} color="#8899aa" variant="body2">
                          • {c}
                        </Typography>
                      ))}
                    </Box>
                  )}
                  {brief.deadline_days && (
                    <Typography color="#d97706" variant="body2" mt={1}>
                      Deadline: {brief.deadline_days} days
                    </Typography>
                  )}
                </Box>
              )}

              {role !== 'owner' && t.status === 'assigned' && (
                <Button
                  variant="contained" size="small"
                  sx={{ background: '#2563eb' }}
                  onClick={() => setDialog({ open: true, taskId: t.id })}
                >
                  Submit Work
                </Button>
              )}
            </CardContent>
          </Card>
        );
      })}

      <Dialog
        open={dialog.open}
        onClose={() => setDialog({ open: false, taskId: null })}
        fullWidth maxWidth="sm"
      >
        <DialogTitle sx={{ background: '#1a2633', color: '#fff' }}>
          Submit Work
        </DialogTitle>
        <DialogContent sx={{ background: '#1a2633' }}>
          <TextField
            fullWidth multiline rows={5}
            label="Describe your submission or paste link"
            value={submission}
            onChange={e => setSubmission(e.target.value)}
            sx={{ mt: 2, textarea: { color: '#fff' }, label: { color: '#8899aa' } }}
          />
        </DialogContent>
        <DialogActions sx={{ background: '#1a2633' }}>
          <Button
            onClick={() => setDialog({ open: false, taskId: null })}
            sx={{ color: '#8899aa' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting || !submission.trim()}
            sx={{ background: '#2563eb' }}
          >
            {submitting ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
